import React, { useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Link } from 'react-router-dom'
import { FlexItem } from 'styled-flex-component'

import { EllipsisWrapper } from '@components/InfluencerIdentity/Row/Details/Details.styled'
import {
  ThumbnailImageIconWrapper,
  ThumbnailImageWrapper,
} from '@components/Submission/Thumbnail/Image/Image.styled'
import Image from '@components/UI/Image'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { IPostCampaignReportSubmissionIdContextProps } from '@context/PostCampaignReportSubmissionId'
import { AspectRatios, EventTrackingNames } from '@enums'
import { withContextPostCampaignReportSubmissionId } from '@hocs'
import { FadeIn } from '@lib/effects'
import { IApplicationState } from '@store'
import {
  selectInfluencerHandle,
  selectInfluencerImageURL,
  selectInfluencerName,
  selectIsCarousel,
  selectSocialPageURL,
  selectSubmissionChannel,
} from '@store/postCampaignReport'
import { selectActivePCRChannel, selectCampaignId } from '@store/router'
import { viewSocial } from '@store/submissions'
import Theme from '@theme'
import { Container, H5, Icon, Link as AnchorLink, Text } from '@tribegroup'
import { ProfileIconWrapper, SubmissionImageWrapper } from './Profile.styled'

export interface IInternalProps {
  influencerName: string
  influencerHandle: string
  submissionImageURL: string
  channel: string
  campaignId: number
  submissionId: number
  socialPageURL: string
  activePCRChannel: string
  isCarousel?: boolean
  viewSocial: typeof viewSocial
}

const PostCampaignReportSubmissionListProfile: React.FC<IInternalProps> = ({
  influencerName,
  influencerHandle,
  submissionImageURL,
  channel,
  campaignId,
  submissionId,
  socialPageURL,
  isCarousel,
  activePCRChannel,
  viewSocial: viewSocialAction,
}) => {
  const onViewSocial = () => viewSocialAction(campaignId, submissionId)
  return (
    <React.Fragment>
      <FlexItem noShrink>
        <ConnectedTrackedRoute
          eventName={EventTrackingNames.ViewSubmission}
          campaignId={campaignId}
          submissionId={submissionId}
          additionalProperties={{
            source: 'campaign_metrics',
          }}
        >
          <Link
            to={`/campaigns/${campaignId}/submissions/${submissionId}?from=${activePCRChannel}`}
          >
            <SubmissionImageWrapper>
              <ThumbnailImageWrapper>
                <Image
                  src={submissionImageURL}
                  aspectRatio={AspectRatios.ONE_BY_ONE}
                  animation={FadeIn}
                  loadingColor={Theme.defaultColor}
                />
                {isCarousel && (
                  <ThumbnailImageIconWrapper>
                    <Icon size={1.25} glyph="carousel" color={Theme.whiteColor} />
                  </ThumbnailImageIconWrapper>
                )}
              </ThumbnailImageWrapper>
            </SubmissionImageWrapper>
          </Link>
        </ConnectedTrackedRoute>
      </FlexItem>
      <FlexItem>
        <EllipsisWrapper color={Theme.defaultColor}>
          <AnchorLink href={socialPageURL} target="_blank" onClick={onViewSocial}>
            <H5 inline color={Theme.defaultColor} rightOuterSpacing={0.75}>
              {influencerName}
            </H5>
          </AnchorLink>
        </EllipsisWrapper>
        <EllipsisWrapper color={Theme.grey700Color}>
          <AnchorLink href={socialPageURL} target="_blank" onClick={onViewSocial}>
            <ProfileIconWrapper rightOuterSpacing={0.25} topOuterSpacing={0.125} inlineBlock>
              <Icon size={0.75} glyph={`social-${channel}-colour`} />
            </ProfileIconWrapper>
            <Container rightOuterSpacing={0.5} inline>
              <Text xsmall color={Theme.grey700Color}>
                {influencerHandle}
              </Text>
            </Container>
          </AnchorLink>
        </EllipsisWrapper>
      </FlexItem>
    </React.Fragment>
  )
}

const PostCampaignReportSubmissionListProfileConnectedInner: React.FC<
  IPostCampaignReportSubmissionIdContextProps
> = ({ contextPostCampaignReportSubmissionId }) => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match)!
  const influencerName = useSelector((state: IApplicationState) =>
    selectInfluencerName(state, contextPostCampaignReportSubmissionId),
  )
  const influencerHandle = useSelector((state: IApplicationState) =>
    selectInfluencerHandle(state, contextPostCampaignReportSubmissionId),
  )
  const submissionImageURL = useSelector((state: IApplicationState) =>
    selectInfluencerImageURL(state, contextPostCampaignReportSubmissionId),
  )
  const socialPageURL = useSelector((state: IApplicationState) =>
    selectSocialPageURL(state, contextPostCampaignReportSubmissionId),
  )
  const channel = useSelector((state: IApplicationState) =>
    selectSubmissionChannel(state, contextPostCampaignReportSubmissionId),
  )
  const isCarousel = useSelector((state: IApplicationState) =>
    selectIsCarousel(state, contextPostCampaignReportSubmissionId),
  )
  const activePCRChannel = useSelector((state: IApplicationState) => selectActivePCRChannel(state))
  const dispatch = useDispatch()
  const viewSocialCallback = useCallback(
    (cid, submissionId) => dispatch(viewSocial(cid, submissionId)),
    [dispatch],
  )
  return (
    <PostCampaignReportSubmissionListProfile
      influencerName={influencerName}
      influencerHandle={influencerHandle}
      submissionImageURL={submissionImageURL}
      socialPageURL={socialPageURL}
      channel={channel}
      campaignId={campaignId}
      submissionId={contextPostCampaignReportSubmissionId}
      isCarousel={isCarousel}
      activePCRChannel={activePCRChannel}
      viewSocial={viewSocialCallback}
    />
  )
}

const PostCampaignReportSubmissionListProfileConnected = withContextPostCampaignReportSubmissionId(
  PostCampaignReportSubmissionListProfileConnectedInner,
)

export { PostCampaignReportSubmissionListProfile }
export default PostCampaignReportSubmissionListProfileConnected
